/**
 * Mint an anonymous storefront session -- the ONE place it happens.
 *
 * Two callers need the same sequence and must not drift:
 *
 *   - StorefrontSessionGate, on entry, when the browser holds no session for
 *     this store (or holds one that has since died);
 *   - the auth interceptor, when an anonymous visitor's session dies
 *     MID-visit (`recoverAnonymousStorefrontSession` below). Jake,
 *     2026-10-01: an expired anonymous visitor is never sent to /login.
 *
 * Both doors are credential-less and return the same three fields
 * (`token`, `account_id`, `session_is_anonymous_storefront`). The template
 * door (`/auth/getToken/<template id>`) stays primary; a store with no
 * template shopper uses `/auth/getStoreToken/<store id>` (PR #3795).
 *
 * The minted token is written to localStorage, and -- once the session is
 * adopted into Vuex -- held in memory too (the Pinia account store). Memory
 * is what keeps the visitor's next request carrying a bearer when the browser
 * drops the storage key (WebKit, prod 2026-10-01 -- see
 * router/sessionProvenance.js `resolveBearer`).
 */
import { getActivePinia } from "pinia";
import { useAccountStore } from "@/stores/account";
import {
  recordSessionProvenance,
  isAnonymousStorefrontToken,
} from "@/router/sessionProvenance";
import {
  markAnonymousStorefrontSession,
  clearAnonymousStorefrontSession,
} from "@/router/anonymousStorefrontSession";
import { SUPPRESS_ERROR_TOAST, SKIP_AUTH_RECOVERY } from "@/interceptors/authResponseInterceptor";
import {
  holdResumeCode,
  forgetResumeCode,
  writeResumeFragment,
  dropResumeFragment,
} from "@/router/storefrontResumeCode";

// Every request this module makes renders its failure ITSELF (the gate's
// retry state, or the interceptor's store-homepage fallback), so a global
// toast on top would be a second, contradictory message. A 401 here is
// likewise ours to handle, never a reason to log the visitor out.
//
// A function, not a module-level constant: it reads the interceptor's keys at
// CALL time, so this module can never capture them half-initialized through
// an import cycle (see the note at the top of router/sessionBearer.js).
export function mintRequestConfig() {
  return { [SUPPRESS_ERROR_TOAST]: true, [SKIP_AUTH_RECOVERY]: true };
}

// Stale user-identity keys that `login` does not overwrite. Cleared before a
// swap so the previous identity's details never leak into the new session.
const STALE_IDENTITY_KEYS = [
  "companyEmail",
  "firstName",
  "lastName",
  "selectedDepartment",
  "selectedClientDepartmentId",
  "selectedManager",
  "selectedLocation",
];

function removeQuietly(key) {
  try {
    localStorage.removeItem(key);
  } catch {
    // Unusable storage holds nothing stale.
  }
}

function setQuietly(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Unusable storage: the session lives in memory for this page.
  }
}

/**
 * POST the store's token door and install the session it returns.
 *
 * @returns {Promise<{ token: string, accountId: (string|number), anonymous: boolean }>}
 * @throws when the door refuses or answers without a token.
 */
export async function mintAnonymousStorefrontSession({
  axiosInstance,
  campaignId,
  templateAccountId,
}) {
  STALE_IDENTITY_KEYS.forEach(removeQuietly);

  // Suppress the global 401 interceptor during the swap (PR #451):
  // concurrent calls that 401 mid-swap must not dispatch logout and clear
  // the new token.
  window._swappingToken = true;
  try {
    const resp = await axiosInstance.post(
      templateAccountId != null
        ? `/auth/getToken/${templateAccountId}`
        : `/auth/getStoreToken/${campaignId}`,
      null,
      mintRequestConfig()
    );
    // A door that answers without a token has not established a session.
    // Treat that as a refusal rather than storing the string "undefined" as
    // a bearer, which would send the visitor into a 401 loop.
    if (resp?.data?.token == null) {
      throw new Error("token door returned no token");
    }
    // Read from the payload rather than hardcoded, so the client cannot
    // drift from what the server stamped on the token row.
    return installMintedSession(resp.data, resp.data.session_is_anonymous_storefront);
  } finally {
    window._swappingToken = false;
  }
}

/**
 * Write a session a door just issued to storage and record its provenance.
 * NOT put in memory here: `adoptStorefrontAccount` does that after the Vuex
 * login (see there). `session` is the payload it hydrates from.
 */
function installMintedSession(data, anonymous) {
  const token = String(data.token).replace(/^["']+|["']+$/g, "");
  const accountId = data.account_id;
  setQuietly("token", token);
  setQuietly("userId", accountId);
  recordSessionProvenance(anonymous);
  return {
    token,
    accountId,
    anonymous: anonymous === true,
    // The fragment resume code an anonymous mint carries (absent on an older
    // API, or on a door that issues none).
    resumeCode: data.resume_code ?? null,
    session: { ...data, token, session_is_anonymous_storefront: anonymous },
  };
}

/** The resume code was refused: the store's uniform 404. */
export class ResumeCodeRefused extends Error {
  constructor() {
    super("resume code refused");
    this.name = "ResumeCodeRefused";
  }
}

/**
 * Trade a fragment resume code for a fresh token on the SAME anonymous
 * account (router/storefrontResumeCode.js). Same shape as a mint.
 *
 * The code only ever restores an ANONYMOUS account for THIS store, so the
 * session is recorded as anonymous. An answer naming another store is
 * refused like a 404 -- the cart page must never be filled from another
 * store's account.
 *
 * @throws ResumeCodeRefused on a 404 or a wrong-store answer; anything else
 *   (5xx, 429, a timeout) is rethrown untouched so the caller can offer a
 *   retry with the code still in hand.
 */
export async function redeemStorefrontResumeCode({ axiosInstance, campaignId, code }) {
  STALE_IDENTITY_KEYS.forEach(removeQuietly);
  window._swappingToken = true;
  try {
    let resp;
    try {
      resp = await axiosInstance.post(
        "/auth/resumeStorefrontSession",
        { store_id: Number(campaignId), code },
        mintRequestConfig()
      );
    } catch (err) {
      if (err?.response?.status === 404) throw new ResumeCodeRefused();
      throw err;
    }
    const data = resp?.data;
    if (
      data?.token == null ||
      data?.account_id == null ||
      String(data.campaign_id) !== String(campaignId)
    ) {
      throw new ResumeCodeRefused();
    }
    return installMintedSession({ ...data, resume_code: code }, true);
  } finally {
    window._swappingToken = false;
  }
}

/** The account adopted belongs to a different store than the one expected. */
export class AccountNotForStore extends Error {
  constructor(accountId, campaignId) {
    super(`account ${accountId} is not a session for store ${campaignId}`);
    this.name = "AccountNotForStore";
  }
}

/**
 * Put the account the session holds into Vuex (`login`), and set or clear
 * the anonymous marker.
 *
 * The marker is DERIVED FROM THE FACT -- the session's recorded provenance --
 * not from whether a swap just happened (GH #2941 defect 2). Read AFTER the
 * `login` dispatch, which is where a session established elsewhere re-learns
 * its provenance; reading before it would see the PREVIOUS session's answer.
 */
export async function adoptStorefrontAccount({
  store,
  accountId,
  requestConfig,
  minted,
  expectCampaignId,
}) {
  const account = await store.dispatch("jv/get", [
    `accounts/${accountId}`,
    { params: { include: "role" }, ...(requestConfig || {}) },
  ]);
  // Checked BEFORE `login`, so a session for another store never becomes
  // this tab's identity even for a moment.
  if (
    expectCampaignId != null &&
    String(account?.campaign_id) !== String(expectCampaignId)
  ) {
    throw new AccountNotForStore(accountId, expectCampaignId);
  }
  store.dispatch("login", account);
  // A freshly minted session goes into memory only NOW, after the Vuex login.
  // Before it, App.vue sees a live in-memory session with no shopper account
  // and treats the visitor as back office for the length of the account
  // round-trip: the version footer flashes and a @login_required back-office
  // setting is requested on an anonymous visitor's bearer (gate bar-raiser
  // P2-2 on #3957).
  if (minted && getActivePinia()) {
    useAccountStore().hydrate(minted);
  }
  if (isAnonymousStorefrontToken()) {
    markAnonymousStorefrontSession();
  } else {
    clearAnonymousStorefrontSession();
  }
  return account;
}

/**
 * Resolve the store a slug names into the inputs the token door needs.
 * Throws on any failure -- including a store that no longer resolves.
 */
export async function resolveStoreDoor({ axiosInstance, slug }) {
  const idResp = await axiosInstance.get(
    `/campaign/getCampaignIdFromShopperURL/${slug}`,
    mintRequestConfig()
  );
  const campaignId = idResp?.data ?? null;
  if (!campaignId) throw new Error(`store "${slug}" did not resolve`);
  const shopperResp = await axiosInstance.get(
    `/campaign/getShopperFromCampaignID/${campaignId}`,
    mintRequestConfig()
  );
  const template = shopperResp?.data;
  return {
    campaignId,
    templateAccountId: template && template.id != null ? template.id : null,
  };
}

let inFlightRecovery = null;

/**
 * Replace a dead anonymous session MID-visit. Single-flight: a burst of
 * concurrent 401s from the dead session shares ONE re-mint.
 *
 * The cart on the dead account is not carried over. Its bearer is gone and
 * nothing server-side lets a new session claim the old row; the visitor
 * keeps browsing on a fresh, empty cart rather than meeting a sign-in page.
 *
 * @returns {Promise<{ oldAccountId, newAccountId }>}
 */
export function recoverAnonymousStorefrontSession({ axiosInstance, store, slug }) {
  if (inFlightRecovery) return inFlightRecovery;

  const oldAccountId =
    store?.state?.account?._jv?.id ??
    (() => {
      try {
        return localStorage.getItem("userId");
      } catch {
        return null;
      }
    })();

  inFlightRecovery = (async () => {
    const door = await resolveStoreDoor({ axiosInstance, slug });
    const minted = await mintAnonymousStorefrontSession({ axiosInstance, ...door });
    await adoptStorefrontAccount({
      store,
      accountId: minted.accountId,
      requestConfig: mintRequestConfig(),
      minted: minted.session,
    });
    // The fragment must name the NEW account: left on the dead one's code, a
    // reload would bring back the old cart instead of the one in use.
    if (minted.resumeCode) {
      holdResumeCode(slug, minted.resumeCode, minted.accountId);
      writeResumeFragment();
    } else {
      forgetResumeCode();
      dropResumeFragment();
    }
    return { oldAccountId, newAccountId: minted.accountId };
  })();

  // Clear the slot whichever way it settles, so a LATER death can recover.
  inFlightRecovery.then(
    () => { inFlightRecovery = null; },
    () => { inFlightRecovery = null; }
  );
  return inFlightRecovery;
}
